import { useRef, useState } from "react";
import { Check, GripVertical, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resetPrototype } from "./mock";

/**
 * ⚠️ PROTOTYPE-ONLY dev control — NOT product functionality.
 *
 * A small floating dev bar with "Reset" (wipes all mock data back to the seeded
 * default state — added / ended / edited contracts revert; the user stays
 * logged in) and a "View gallery" link to the review gallery.
 *
 * It is mounted globally ONLY when the dev auth bypass is active and never on
 * the gallery route itself (see App.tsx) — the same gating as devBootstrap /
 * the dev API mock, so it can never reach production and is not woven into any
 * contract feature component.
 *
 * Default position is top-left; it is draggable by the grip handle and its
 * position persists across reloads.
 */
const POS_KEY = "tcf-reset-button-pos";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

export function PrototypeResetButton() {
  const [pos, setPos] = useState(loadPos);
  const [done, setDone] = useState(false);
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(
    null,
  );

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    d.moved = true;
    const x = Math.max(0, Math.min(window.innerWidth - 96, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - 40, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current?.moved) localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const handleReset = () => {
    if (done) return;
    resetPrototype();
    // Brief visual confirmation before the reload re-seeds the mock.
    setDone(true);
    window.setTimeout(() => window.location.reload(), 650);
  };

  return (
    <div
      className="fixed z-[100] flex items-center gap-1 rounded-md border border-dashed border-orange-border bg-background px-1.5 py-1 shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* dev/prototype marker dot */}
      <span aria-hidden className="ml-0.5 size-1.5 shrink-0 rounded-full bg-orange-foreground" />
      <button
        type="button"
        aria-label="Drag to move"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="flex cursor-grab touch-none items-center text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="pl-1.5"
        onClick={handleReset}
        title="Reset prototype to the seeded data (you stay logged in)"
      >
        {done ? (
          <>
            <Check className="size-4 text-teal-foreground" />
            <span className="text-teal-foreground">Reset!</span>
          </>
        ) : (
          <>
            <RotateCcw className="size-4 text-muted-foreground" />
            Reset
          </>
        )}
      </Button>
      <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
      <a
        href="/gallery"
        title="What changed in this version"
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground hover:bg-faint hover:text-foreground"
      >
        What changed
      </a>
    </div>
  );
}
